import { useEffect, useState } from 'react';
import { permissionsApiService } from '../../api/permissionsApiService';
import {
  tPermission,
  initialPermissionsData,
  PERMISSION_KEY_MAP,
} from '../../hooks/usePermission';

type PermissionsData = typeof initialPermissionsData;

/**
 * Displays the "Uprawnienia" tab of the View Client page.
 *
 * Fetches the active permission template on mount via `permissionsApiService`,
 * merges the allowed flags into `initialPermissionsData`, and renders each
 * module's enabled permissions as labelled chip badges.
 *
 * Handles loading (skeleton placeholders), error, and empty-permissions states.
 * Modules with no checked permissions are hidden from the rendered output.
 */
const ViewClientPermissions = () => {
  const [permissions, setPermissions] = useState<PermissionsData>(
    initialPermissionsData
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    permissionsApiService
      .getTemplate()
      .then((result) => {
        if (result.success && result.template) {
          const allowedMap: Record<string, boolean> = {};
          for (const privilege of result.template.privileges) {
            for (const perm of privilege.permissions) {
              allowedMap[perm.key] = perm.allowed;
            }
          }
          setPermissions(
            initialPermissionsData.map((module) => ({
              ...module,
              options: module.options.map((opt) => {
                if (opt.disabled) {
                  return opt;
                }
                const backendKey = PERMISSION_KEY_MAP[opt.value];
                if (!backendKey) {
                  return opt;
                }
                return { ...opt, checked: allowedMap[backendKey] ?? false };
              }),
            })) as PermissionsData
          );
        } else if (!result.success) {
          setError(result.error ?? 'Failed to load permissions.');
        }
      })
      .catch(() => setError('Failed to load permissions.'))
      .finally(() => setIsLoading(false));
  }, []);

  const modulesWithChecked = permissions
    .map((module) => ({
      title: module.title,
      checked: module.options.filter((o) => o.checked),
    }))
    .filter((module) => module.checked.length > 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-titleDefault font-medium text-on-surface-variant"
          data-testid="view-client-permissions-title"
        >
          Uprawnienia
        </p>

        {isLoading ? (
          <div
            className="flex flex-col gap-6"
            data-testid="view-client-permissions-loading"
          >
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col gap-2">
                <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 4 - (i % 2) }).map((_, j) => (
                    <div
                      key={j}
                      className="h-8 bg-gray-200 rounded-[8px] animate-pulse"
                      style={{ width: `${72 + ((j * 16) % 48)}px` }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <p
            className="text-bodyMedium text-on-surface-variant"
            data-testid="view-client-permissions-error"
          >
            {error}
          </p>
        ) : modulesWithChecked.length === 0 ? (
          <p
            className="text-bodyMedium text-on-surface-variant"
            data-testid="view-client-permissions-empty"
          >
            No permissions assigned.
          </p>
        ) : (
          <div className="flex flex-col gap-8">
            {modulesWithChecked.map((module) => (
              <div key={module.title} className="flex flex-col gap-3">
                <p className="text-bodySmall font-normal text-on-surface-variant">
                  {tPermission(module.title)}
                </p>
                <div className="flex flex-wrap gap-2">
                  {module.checked.map((opt) => (
                    <span
                      key={opt.value}
                      className="inline-flex items-center justify-center h-8 px-3 rounded-[8px] bg-information-90 text-information-30 text-labelLarge"
                      data-testid={`view-client-permission-${opt.value}`}
                    >
                      {tPermission(opt.label)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewClientPermissions;
